const CUSTOM_FEEDBACK_OPTIONS = {
  NONE: {
    label: 'None',
    value: 'NONE',
    description: 'No feedback message is displayed to the user after they submit their answer.'
  },
  GENERAL: {
    label: 'General',
    value: 'GENERAL',
    description: 'A general feedback message that is displayed to the user after they submit their answer, regardless of the answer they provided.'
  },
  SPECIFIC: {
    label: 'Specific',
    value: 'SPECIFIC',
    description: 'A specific feedback message that is displayed to the user after they submit their answer, based if answers are correct or incorrect.'
  }
}

/**
 * Validates a question block data
 * @param {Object} questionData - The question block data
 * @returns {Array} - Array of validation error messages
 */
const validateQuestionBlock = (questionData) => {
  const errors = []

  // Check if question body exists and has content
  const hasQuestionBody = questionData.questionBody &&
    (typeof questionData.questionBody === 'string'
      ? questionData.questionBody.trim() !== ''
      : questionData.questionBody.blocks &&
        questionData.questionBody.blocks.length > 0 &&
        questionData.questionBody.blocks.some(block => block.text && block.text.trim() !== ''))

  if (!hasQuestionBody) {
    errors.push('Question body cannot be empty')
  }

  // Check if at least two answer options exist
  const answerOptions = questionData.answerOptions || []
  if (answerOptions.length < 2) {
    errors.push('At least two answer options are required')
  }

  // Check if all answer options have text
  const hasEmptyOptions = answerOptions.some(option => !option.label || option.label.trim() === '')
  if (hasEmptyOptions) {
    errors.push('All answer options must have text')
  }

  // Check if at least one correct answer is selected
  const hasCorrectAnswer = answerOptions.some(option => option.isOptionCorrect === true)
  if (!hasCorrectAnswer) {
    errors.push('At least one correct answer must be selected')
  }

  // Validate custom feedback based on feedback type
  const customFeedback = questionData.customFeedback || {}
  const feedbackType = customFeedback.feedbackType

  if (feedbackType === CUSTOM_FEEDBACK_OPTIONS.GENERAL.value) {
    const hasGeneralFeedback = customFeedback.generalFeedback &&
      (typeof customFeedback.generalFeedback === 'string'
        ? customFeedback.generalFeedback.trim() !== ''
        : customFeedback.generalFeedback.blocks &&
          customFeedback.generalFeedback.blocks.length > 0 &&
          customFeedback.generalFeedback.blocks.some(block => block.text && block.text.trim() !== ''))

    if (!hasGeneralFeedback) {
      errors.push('General feedback cannot be empty when General feedback type is selected')
    }
  }

  if (feedbackType === CUSTOM_FEEDBACK_OPTIONS.SPECIFIC.value) {
    const hasCorrectFeedback = customFeedback.correctFeedback &&
      (typeof customFeedback.correctFeedback === 'string'
        ? customFeedback.correctFeedback.trim() !== ''
        : customFeedback.correctFeedback.blocks &&
          customFeedback.correctFeedback.blocks.length > 0 &&
          customFeedback.correctFeedback.blocks.some(block => block.text && block.text.trim() !== ''))

    const hasIncorrectFeedback = customFeedback.incorrectFeedback &&
      (typeof customFeedback.incorrectFeedback === 'string'
        ? customFeedback.incorrectFeedback.trim() !== ''
        : customFeedback.incorrectFeedback.blocks &&
          customFeedback.incorrectFeedback.blocks.length > 0 &&
          customFeedback.incorrectFeedback.blocks.some(block => block.text && block.text.trim() !== ''))

    if (!hasCorrectFeedback) {
      errors.push('Correct answer feedback cannot be empty when Specific feedback type is selected')
    }

    if (!hasIncorrectFeedback) {
      errors.push('Incorrect answer feedback cannot be empty when Specific feedback type is selected')
    }
  }

  return errors
}

/**
 * Validates all question blocks in the content body
 * @param {Object} body - The content body containing Draft.js content
 * @returns {Object} - { isValid: boolean, errors: Array }
 */
export const validateContentQuestions = (body) => {
  // Check if body has the expected structure
  const draftJsContent = body?.data || body

  if (!draftJsContent || !draftJsContent.entityMap) {
    return { isValid: true, errors: [] }
  }

  const allErrors = []
  const entityMap = draftJsContent.entityMap

  // Iterate through all entities to find question blocks
  Object.keys(entityMap).forEach((entityKey) => {
    const entity = entityMap[entityKey]

    if (entity.type === 'ew-question') {
      const questionData = entity.data
      const questionErrors = validateQuestionBlock(questionData)

      if (questionErrors.length > 0) {
        allErrors.push(...questionErrors)
      }
    }
  })

  return {
    isValid: allErrors.length === 0,
    errors: allErrors
  }
}

/**
 * Checks if content has any question blocks
 * @param {Object} body - The content body
 * @returns {boolean}
 */
export const contentHasQuestions = (body) => {
  // Check if body has the expected structure
  const draftJsContent = body?.data || body

  if (!draftJsContent || !draftJsContent.entityMap) {
    return false
  }

  const entityMap = draftJsContent.entityMap
  return Object.keys(entityMap).some(key => entityMap[key].type === 'ew-question')
}
